import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import API from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Plus, Minus, CreditCard, Loader2, CheckCircle, Package, Crown, Car, ArrowRight, Sparkles
} from 'lucide-react';

export default function BuySlots() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(true);

  const SLOT_PRICE = 10; // GHS per slot
  const totalAmount = quantity * SLOT_PRICE;

  useEffect(() => {
    const fetchEligibility = async () => {
      try {
        const res = await API.get('/listings/eligibility');
        setEligibility(res.data);
      } catch (error) {
        console.error('Error fetching eligibility:', error);
      } finally {
        setCheckingEligibility(false);
      }
    };
    fetchEligibility();
  }, []);

  useEffect(() => {
    // Check for payment verification callback from Paystack
    const verifyRef = searchParams.get('verify');
    if (verifyRef) {
      verifyPurchase(verifyRef);
    }
  }, [searchParams]);

  const verifyPurchase = async (reference) => {
    setLoading(true);
    try {
      const res = await API.get(`/slots/verify/${reference}`);
      if (res.data.status === 'success') {
        toast.success(res.data.message || `Successfully added listing slot(s)!`);
        // Refresh eligibility
        const eligRes = await API.get('/listings/eligibility');
        setEligibility(eligRes.data);
        // Clear the URL params
        navigate('/buy-slots', { replace: true });
      } else if (res.data.status === 'completed') {
        toast.info('Slots already credited to your account');
        const eligRes = await API.get('/listings/eligibility');
        setEligibility(eligRes.data);
        navigate('/buy-slots', { replace: true });
      } else {
        toast.error(res.data.message || 'Payment verification pending');
      }
    } catch (error) {
      console.error('Error verifying purchase:', error);
      toast.error('Failed to verify payment. Please contact support.');
    } finally {
      setLoading(false);
    }
  };

  const purchaseSlots = async () => {
    setLoading(true);
    try {
      const res = await API.post('/slots/purchase', { quantity });
      if (res.data.authorization_url) {
        window.location.href = res.data.authorization_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  if (checkingEligibility) {
    return (
      <main className="min-h-screen bg-background py-10 px-4">
        <div className="max-w-2xl mx-auto flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-gold" />
        </div>
      </main>
    );
  }

  // If user is a dealer or admin, they don't need slots
  if (eligibility?.is_dealer || eligibility?.is_admin) {
    return (
      <main className="min-h-screen bg-background py-10 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-card border border-border/50 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-trust-green/20 flex items-center justify-center mx-auto mb-6">
              <Crown className="w-8 h-8 text-trust-green" />
            </div>
            <h1 className="text-2xl font-bold mb-3">
              {eligibility?.is_admin ? "Admin Access" : "You're a Dealer!"}
            </h1>
            <p className="text-muted-foreground mb-6">
              {eligibility?.is_admin 
                ? "As an admin, you have unlimited listings. No need to purchase additional slots."
                : "As a dealer, you have unlimited listings. No need to purchase additional slots."
              }
            </p>
            <Button onClick={() => navigate('/create-listing')} className="bg-gold text-black">
              <Car className="w-4 h-4 mr-2" />
              Create New Listing
            </Button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background py-10 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-gold" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Buy Listing Slots</h1>
          <p className="text-muted-foreground">
            Purchase additional slots to list more cars without becoming a dealer
          </p>
        </motion.div>

        {/* Current Slots Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-card border border-border/50 rounded-xl p-5 mb-6"
        >
          <h3 className="font-medium mb-4">Your Current Slots</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold">{eligibility?.base_slots || 3}</p>
              <p className="text-xs text-muted-foreground">Free Slots</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gold">{eligibility?.purchased_slots || 0}</p>
              <p className="text-xs text-muted-foreground">Purchased</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-trust-green">{eligibility?.remaining_slots || 0}</p>
              <p className="text-xs text-muted-foreground">Available</p>
            </div>
          </div>
        </motion.div>

        {/* Slot Purchase Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-card border border-border/50 rounded-2xl overflow-hidden"
          data-testid="slot-purchase-card"
        >
          <div className="bg-gradient-to-r from-gold/20 to-gold/5 p-6 border-b border-border/50">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xl font-bold">Listing Slot</h2>
              <Badge className="bg-trust-green text-white border-none">
                <Sparkles className="w-3 h-3 mr-1" /> Never Expires
              </Badge>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold text-gold">GHS {SLOT_PRICE}</span>
              <span className="text-muted-foreground">/ slot</span>
            </div>
          </div>

          <div className="p-6">
            {/* Quantity Selector */}
            <div className="mb-6">
              <label className="text-sm font-medium mb-2 block">Quantity</label>
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                  data-testid="decrease-qty"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="text-3xl font-bold w-16 text-center" data-testid="quantity">{quantity}</span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setQuantity(Math.min(20, quantity + 1))}
                  disabled={quantity >= 20}
                  data-testid="increase-qty"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Max 20 slots per purchase</p>
            </div>

            {/* Quick Select */}
            <div className="flex gap-2 mb-6">
              {[1, 3, 5, 10].map((num) => (
                <button
                  key={num}
                  onClick={() => setQuantity(num)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                    quantity === num
                      ? 'bg-gold text-black'
                      : 'bg-background border border-border/50 hover:border-gold/30'
                  }`}
                >
                  {num} slot{num > 1 ? 's' : ''}
                </button>
              ))}
            </div>

            {/* Total */}
            <div className="bg-background border border-border/50 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Total</span>
                <span className="text-2xl font-bold text-gold" data-testid="total-amount">
                  GHS {totalAmount.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Payment Methods */}
            <div className="mb-6">
              <p className="text-sm text-muted-foreground mb-3">Pay with MTN MoMo, Vodafone Cash, or Card</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                  <span className="text-yellow-500 font-bold text-sm">MTN</span>
                </div>
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <span className="text-red-500 font-bold text-xs">Voda</span>
                </div>
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-blue-500" />
                </div>
              </div>
            </div>

            {/* Purchase Button */}
            <Button
              onClick={purchaseSlots}
              disabled={loading}
              className="w-full h-12 bg-gold text-black hover:brightness-110 gap-2"
              data-testid="purchase-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4" />
              )}
              Buy {quantity} Slot{quantity > 1 ? 's' : ''} - GHS {totalAmount}
            </Button>

            <p className="text-xs text-muted-foreground text-center mt-4">
              Secure payment powered by Paystack. Slots never expire.
            </p>
          </div>
        </motion.div>

        {/* Dealer Alternative */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-6 bg-gradient-to-r from-gold/10 to-gold/5 border border-gold/30 rounded-xl p-5"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
                <Crown className="w-5 h-5 text-gold" />
              </div>
              <div>
                <h3 className="font-semibold">Want Unlimited Listings?</h3>
                <p className="text-sm text-muted-foreground">Become a dealer from GHS 50/mo</p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/dealer')}
              variant="outline"
              className="border-gold/50 text-gold hover:bg-gold/10 gap-2"
            >
              Learn More <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </motion.div>
      </div>
    </main>
  );
}
